import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getOrganization, type ErrorResponse, type UserOrganization } from '@vn-propane/shared';
import { cn } from '@/lib/utils';
import { LanguageSelector } from '@/components/ui/language-selector';
import { User, CircleHelp, Menu, X } from 'lucide-react';

const navItems = [
  { path: '', labelKey: 'navigation.gasStations', end: true },
  { path: 'drivers', labelKey: 'navigation.deliveryDrivers' },
  { path: 'customers', labelKey: 'navigation.customers' },
  { path: 'products', labelKey: 'navigation.products' },
  { path: 'orders', labelKey: 'navigation.orders' },
  { path: 'reports', labelKey: 'navigation.reports' },
  { path: 'members', labelKey: 'navigation.members' },
  { path: 'settings', labelKey: 'navigation.settings' },
];

export default function OrganizationLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation('common');
  const [organization, setOrganization] = useState<UserOrganization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on navigation
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    async function fetchOrganization() {
      if (!id) return;

      try {
        const result = await getOrganization({
          path: { id },
        });

        if (result.error) {
          const errorResponse = result.error as ErrorResponse;
          if (result.response?.status === 401) {
            navigate('/login');
            return;
          }
          if (result.response?.status === 404) {
            navigate('/organizations');
            return;
          }
          console.error(errorResponse.error?.message);
          return;
        }

        if (result.data) {
          setOrganization(result.data.data);
        }
      } catch (err) {
        console.error('Failed to load organization');
      } finally {
        setIsLoading(false);
      }
    }

    fetchOrganization();
  }, [id, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/40">
        <div className="animate-pulse">
          <div className="h-14 md:h-16 bg-background border-b" />
          <div className="flex">
            <div className="w-56 h-screen bg-background border-r hidden md:block" />
            <div className="flex-1 p-4 md:p-8">
              <div className="h-8 bg-muted rounded w-1/4 mb-4" />
              <div className="h-64 bg-muted rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return null;
  }

  return (
    <div className="min-h-screen bg-muted/40">
      {/* Header */}
      <header className="h-14 md:h-16 bg-background border-b flex items-center px-3 md:px-6 justify-between sticky top-0 z-50">
        <div className="flex items-center gap-2 md:gap-4 min-w-0">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden p-2 -ml-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
            aria-label="Toggle menu"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <button
            onClick={() => navigate('/organizations')}
            className="hidden md:flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <span>&larr; {t('navigation.allOrganizations')}</span>
          </button>
          <span className="text-muted-foreground hidden md:inline">/</span>
          <h1 className="text-base md:text-lg font-semibold truncate tracking-tight">{organization.name}</h1>
          <span className="text-[10px] uppercase tracking-widest font-semibold bg-muted text-muted-foreground px-2 py-1 rounded-sm hidden sm:inline">
            {organization.role}
          </span>
        </div>
        <div className="flex items-center gap-1 md:gap-2 shrink-0">
          <LanguageSelector />
          <button
            onClick={() => navigate('/account')}
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={t('navigation.accountSettings')}
          >
            <User className="h-5 w-5" />
          </button>
        </div>
      </header>

      <div className="flex relative">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar — dark carbon (matches Giao Gas admin UI kit) */}
        <nav className={cn(
          'fixed md:static z-40 md:z-auto top-14 md:top-auto left-0 w-56 bg-sidebar text-sidebar-foreground border-r border-sidebar-border h-[calc(100vh-3.5rem)] md:h-auto md:min-h-[calc(100vh-4rem)] p-3 flex flex-col transition-transform duration-200 md:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}>
          <div className="hidden md:flex items-center gap-2 px-2 pb-3 mb-2 border-b border-sidebar-border">
            <img src={`${import.meta.env.BASE_URL}logo-mark-light.png`} alt="" className="h-7 w-7" />
            <div className="text-[17px] font-extrabold tracking-tight leading-none">
              giao<span className="text-propane-500">gas</span>
            </div>
          </div>
          <ul className="space-y-1 flex-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'block px-3 py-2.5 rounded-sm text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                        : 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent'
                    )
                  }
                >
                  {t(item.labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
          <button
            onClick={() => navigate('/organizations')}
            className="flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors md:hidden mb-2"
          >
            &larr; {t('navigation.allOrganizations')}
          </button>
          <a
            href={i18n.language === 'en' ? `${import.meta.env.VITE_HELP_URL || '/help'}/en/admin/` : `${import.meta.env.VITE_HELP_URL || '/help'}/admin/`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
          >
            <CircleHelp className="h-4 w-4" />
            {t('navigation.help')}
          </a>
        </nav>

        {/* Main content */}
        <main className="flex-1 p-4 md:p-8 min-w-0">
          <Outlet context={{ organization }} />
        </main>
      </div>
    </div>
  );
}
